import React, {useCallback, useEffect, useMemo, useState} from 'react'
import {
  Box,
  CircularProgress,
  Grid,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material'
import {format, subDays, differenceInDays, parseISO} from 'date-fns'
import {useForm} from 'react-hook-form'
import {yupResolver} from '@hookform/resolvers/yup'
import * as Yup from 'yup'

import PageTitle from '../../components/PageTitle'
import {FormProvider, RHFDateField} from '../../common/hook-form'
import {usePaceShipmentCounts} from './usePaceShipmentCounts'
import {
  PageWrapper,
  FilterCard,
  FilterHeader,
  FilterIconBox,
  FilterTitle,
  FilterBody,
  RunBtn,
  KpiGrid,
  KpiCard,
  KpiTop,
  KpiLabel,
  KpiIcon,
  KpiValue,
  KpiSub,
  KpiInfoBtn,
  SectionCard,
  SectionHeader,
  SectionHeaderLeft,
  SectionTitle,
  SectionBadge,
  OrdersFilterBar,
  SearchInput,
  StyledTable,
  StyledTh,
  StyledTr,
  StyledTd,
  CellBar,
  PctChip,
  PaginationRow,
  PaginationInfo,
  PaginationBtns,
  VisualBarRow,
  VisualBarName,
  VisualBarTrack,
  VisualBarFill,
  VisualBarValue,
  LoadingOverlay,
  EmptyState,
  EmptyText,
  WelcomeCard,
  WelcomeIconBox,
  WelcomeTitle,
  WelcomeSub,
} from '../pace_analytics/styled'

import TuneIcon from '@mui/icons-material/Tune'
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import LocalShippingIcon from '@mui/icons-material/LocalShipping'
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined'
import SpeedIcon from '@mui/icons-material/Speed'
import StorefrontIcon from '@mui/icons-material/Storefront'
import BarChartIcon from '@mui/icons-material/BarChart'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'

const PAGE_SIZE = 10
const MAX_DAYS = 15

const PRESET_DAYS = [7, 15]

const MERCHANT_COLORS = [
  '#1565c0', '#2e7d32', '#f57f17', '#c62828',
  '#7b1fa2', '#00838f', '#ad1457', '#4e342e',
  '#1b5e20', '#e65100',
]

const schema = Yup.object({
  fromDate: Yup.date().required('Required').nullable(),
  toDate: Yup.date()
    .required('Required')
    .nullable()
    .min(Yup.ref('fromDate'), 'End date must be after start date')
    .test('max-range', `Max range is ${MAX_DAYS} days`, function (toDate) {
      const {fromDate} = this.parent
      if (!fromDate || !toDate) return true
      return differenceInDays(toDate, fromDate) + 1 <= MAX_DAYS
    }),
})

function buildDefaultValues(days) {
  const toDate = new Date()
  const fromDate = subDays(toDate, days - 1)
  return {fromDate, toDate}
}

export default function PaceShipmentCounts() {
  const {isLoadingSummary, isLoadingMerchants, summary, merchantData, loadAll} = usePaceShipmentCounts()
  const [hasLoaded, setHasLoaded] = useState(false)
  const [activePreset, setActivePreset] = useState(7)
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(0)

  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues: buildDefaultValues(7),
  })
  const {handleSubmit, setValue, formState: {errors}} = methods

  const applyPreset = useCallback(
    (days) => {
      const {fromDate, toDate} = buildDefaultValues(days)
      setValue('fromDate', fromDate)
      setValue('toDate', toDate)
      setActivePreset(days)
    },
    [setValue]
  )

  const onSubmit = useCallback(
    (values) => {
      const fromDate = format(values.fromDate, 'yyyy-MM-dd')
      const toDate = format(values.toDate, 'yyyy-MM-dd')
      setHasLoaded(true)
      loadAll({fromDate, toDate})
    },
    [loadAll]
  )

  // Auto-load on mount with default 7-day preset
  useEffect(() => {
    const {fromDate, toDate} = buildDefaultValues(7)
    setHasLoaded(true)
    loadAll({
      fromDate: format(fromDate, 'yyyy-MM-dd'),
      toDate: format(toDate, 'yyyy-MM-dd'),
    })
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const dates = merchantData?.dates || []
  const merchants = merchantData?.merchants || []

  const filteredMerchants = useMemo(() => {
    if (!search.trim()) return merchants
    const q = search.toLowerCase()
    return merchants.filter(m => m.merchantName?.toLowerCase().includes(q))
  }, [merchants, search])

  const pageCount = Math.ceil(filteredMerchants.length / PAGE_SIZE)
  const pagedMerchants = filteredMerchants.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE)

  const totalsRow = useMemo(() => {
    if (!dates.length || !merchants.length) return {}
    return dates.reduce((acc, d) => {
      acc[d] = merchants.reduce((s, m) => s + (m.byDate?.[d] || 0), 0)
      return acc
    }, {})
  }, [dates, merchants])

  const grandTotal = merchants.reduce((s, m) => s + (m.total || 0), 0)
  const topMerchantsForChart = merchants.slice(0, 10)
  const maxMerchantTotal = topMerchantsForChart[0]?.total || 1

  const dateRangeError = errors.toDate?.message

  return (
    <PageWrapper>
      <PageTitle
        title='PACE · Shipment Counts'
        subtitle='Daily PACE order volume by merchant and courier'
      />

      {/* ── Filter Card ──────────────────────────────────────────────────── */}
      <FilterCard>
        <FilterHeader>
          <FilterIconBox>
            <TuneIcon sx={{fontSize: 16}} />
          </FilterIconBox>
          <FilterTitle>Date Range</FilterTitle>
          <Box sx={{ml: 'auto', display: 'flex', gap: 1}}>
            {PRESET_DAYS.map(d => (
              <Box
                key={d}
                onClick={() => applyPreset(d)}
                sx={{
                  px: 1.5, py: 0.5, borderRadius: 1.5, fontSize: '0.72rem',
                  fontWeight: 600, cursor: 'pointer', transition: 'all .15s',
                  backgroundColor: activePreset === d ? '#1565c0' : '#f5f5f5',
                  color: activePreset === d ? '#fff' : '#757575',
                  '&:hover': {backgroundColor: activePreset === d ? '#1565c0' : '#e0e0e0'},
                }}
              >
                Last {d} Days
              </Box>
            ))}
          </Box>
        </FilterHeader>
        <FilterBody>
          <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={2} alignItems='flex-start'>
              <Grid item xs={12} sm={4} md={3}>
                <RHFDateField name='fromDate' label='From Date' />
              </Grid>
              <Grid item xs={12} sm={4} md={3}>
                <RHFDateField name='toDate' label='To Date' />
              </Grid>
              <Grid item xs={12} sm={4} md={3} sx={{display: 'flex', alignItems: 'flex-end', pb: 0.5}}>
                <RunBtn type='submit' variant='contained' startIcon={<CalendarTodayIcon sx={{fontSize: 15}} />}>
                  Apply
                </RunBtn>
              </Grid>
            </Grid>
            {dateRangeError && (
              <Box sx={{display: 'flex', alignItems: 'center', gap: 0.75, mt: 1}}>
                <WarningAmberIcon sx={{fontSize: 14, color: '#e65100'}} />
                <Typography sx={{fontSize: '0.72rem', color: '#e65100', fontWeight: 600}}>
                  {dateRangeError}
                </Typography>
              </Box>
            )}
            <Typography sx={{fontSize: '0.65rem', color: '#9e9e9e', mt: 0.75}}>
              Maximum allowed range: {MAX_DAYS} days
            </Typography>
          </FormProvider>
        </FilterBody>
      </FilterCard>

      {/* ── Not yet loaded ───────────────────────────────────────────────── */}
      {!hasLoaded && (
        <WelcomeCard>
          <WelcomeIconBox><SmartToyOutlinedIcon /></WelcomeIconBox>
          <WelcomeTitle>Select a date range to view PACE shipment data</WelcomeTitle>
          <WelcomeSub>Choose a preset or pick custom dates above and click Apply.</WelcomeSub>
        </WelcomeCard>
      )}

      {hasLoaded && (
        <>
          {/* ── KPI Cards ──────────────────────────────────────────────────── */}
          <KpiGrid>
            <KpiCard>
              <KpiTop>
                <KpiLabel>Total Orders</KpiLabel>
                <KpiIcon bg='#e3f2fd'>
                  <LocalShippingIcon sx={{fontSize: 14, color: '#1565c0'}} />
                </KpiIcon>
              </KpiTop>
              {isLoadingSummary ? (
                <CircularProgress size={20} sx={{mt: 1}} />
              ) : (
                <>
                  <KpiValue>{summary?.totalOrders?.toLocaleString() ?? '—'}</KpiValue>
                  <KpiSub>All shipments in range</KpiSub>
                </>
              )}
            </KpiCard>

            <KpiCard>
              <KpiTop>
                <KpiLabel>PACE Orders</KpiLabel>
                <KpiIcon bg='#e8f5e9'>
                  <SmartToyOutlinedIcon sx={{fontSize: 14, color: '#2e7d32'}} />
                </KpiIcon>
              </KpiTop>
              {isLoadingSummary ? (
                <CircularProgress size={20} sx={{mt: 1}} />
              ) : (
                <>
                  <KpiValue>{summary?.totalPaceOrders?.toLocaleString() ?? '—'}</KpiValue>
                  <KpiSub>Allocated via PACE engine</KpiSub>
                </>
              )}
            </KpiCard>

            <KpiCard>
              <KpiTop>
                <KpiLabel>PACE %</KpiLabel>
                <KpiIcon bg='#f3e5f5'>
                  <SpeedIcon sx={{fontSize: 14, color: '#7b1fa2'}} />
                </KpiIcon>
              </KpiTop>
              {isLoadingSummary ? (
                <CircularProgress size={20} sx={{mt: 1}} />
              ) : (
                <>
                  <KpiValue>
                    {summary?.pacePercentage != null ? `${summary.pacePercentage.toFixed(2)}%` : '—'}
                  </KpiValue>
                  <KpiSub>Share of total shipments</KpiSub>
                </>
              )}
            </KpiCard>

            <KpiCard>
              <KpiTop>
                <KpiLabel>Active PACE Brands</KpiLabel>
                <KpiIcon bg='#fff3e0'>
                  <StorefrontIcon sx={{fontSize: 14, color: '#e65100'}} />
                </KpiIcon>
              </KpiTop>
              {isLoadingSummary ? (
                <CircularProgress size={20} sx={{mt: 1}} />
              ) : (
                <>
                  <KpiValue>{summary?.activePaceBrands ?? '—'}</KpiValue>
                  <KpiSub>Brands with ≥1 PACE order</KpiSub>
                </>
              )}
            </KpiCard>
          </KpiGrid>

          {/* ── Top Merchants Bar Chart ─────────────────────────────────────── */}
          {!isLoadingMerchants && topMerchantsForChart.length > 0 && (
            <SectionCard>
              <SectionHeader>
                <SectionHeaderLeft>
                  <Box sx={{
                    width: 32, height: 32, borderRadius: 2,
                    backgroundColor: '#e3f2fd', display: 'flex',
                    alignItems: 'center', justifyContent: 'center', color: '#1565c0',
                  }}>
                    <BarChartIcon sx={{fontSize: 16}} />
                  </Box>
                  <Box>
                    <SectionTitle>Top Merchants — PACE Orders</SectionTitle>
                    <Typography sx={{fontSize: '0.68rem', color: '#9e9e9e', mt: 0.25}}>
                      Top 10 brands by PACE shipment count
                    </Typography>
                  </Box>
                </SectionHeaderLeft>
                <SectionBadge>{merchants.length} Merchants</SectionBadge>
              </SectionHeader>
              <Box sx={{p: 2.5}}>
                {topMerchantsForChart.map((m, idx) => (
                  <VisualBarRow key={m.merchantName}>
                    <VisualBarName>{m.merchantName}</VisualBarName>
                    <VisualBarTrack>
                      <VisualBarFill
                        pct={Math.round((m.total / maxMerchantTotal) * 100)}
                        bg={MERCHANT_COLORS[idx % MERCHANT_COLORS.length]}
                      />
                    </VisualBarTrack>
                    <VisualBarValue>{m.total?.toLocaleString()}</VisualBarValue>
                  </VisualBarRow>
                ))}
              </Box>
            </SectionCard>
          )}

          {/* ── Merchant × Day Pivot Table ──────────────────────────────────── */}
          <SectionCard>
            <SectionHeader>
              <SectionHeaderLeft>
                <Box sx={{
                  width: 32, height: 32, borderRadius: 2,
                  backgroundColor: '#e8f5e9', display: 'flex',
                  alignItems: 'center', justifyContent: 'center', color: '#2e7d32',
                }}>
                  <StorefrontIcon sx={{fontSize: 16}} />
                </Box>
                <Box>
                  <SectionTitle>
                    Merchant PACE Shipment Count
                    <Tooltip
                      title='PACE shipments per merchant per day. Bar = share vs top merchant. % = share of total PACE orders.'
                      placement='top'
                    >
                      <InfoOutlinedIcon sx={{fontSize: 13, color: '#bdbdbd', ml: 0.75, cursor: 'help', verticalAlign: 'middle'}} />
                    </Tooltip>
                  </SectionTitle>
                  <Typography sx={{fontSize: '0.68rem', color: '#9e9e9e', mt: 0.25}}>
                    PACE shipments per merchant per day
                  </Typography>
                </Box>
              </SectionHeaderLeft>
              <SectionBadge>{merchants.length} Merchants</SectionBadge>
            </SectionHeader>

            <OrdersFilterBar>
              <SearchInput
                placeholder='Search by merchant…'
                value={search}
                onChange={e => { setSearch(e.target.value); setPage(0) }}
              />
            </OrdersFilterBar>

            {isLoadingMerchants ? (
              <LoadingOverlay><CircularProgress size={28} /></LoadingOverlay>
            ) : dates.length === 0 ? (
              <EmptyState><EmptyText>No data for the selected range</EmptyText></EmptyState>
            ) : (
              <Box sx={{overflowX: 'auto'}}>
                <StyledTable>
                  <thead>
                    <tr>
                      <StyledTh>Merchant</StyledTh>
                      <StyledTh align='right'>Total</StyledTh>
                      {dates.map(d => (
                        <StyledTh key={d} align='right'>
                          {format(parseISO(d), 'dd/MM')}
                        </StyledTh>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {/* Totals row */}
                    <StyledTr>
                      <StyledTd bold>
                        <Box
                          sx={{
                            display: 'inline-flex', px: 1, py: 0.25, borderRadius: 1,
                            backgroundColor: '#1565c0', color: '#fff',
                            fontSize: '0.75rem', fontWeight: 700,
                          }}
                        >
                          Totals
                        </Box>
                      </StyledTd>
                      <StyledTd align='right' bold>{grandTotal.toLocaleString()}</StyledTd>
                      {dates.map(d => (
                        <StyledTd key={d} align='right' bold>
                          {(totalsRow[d] || 0).toLocaleString()}
                        </StyledTd>
                      ))}
                    </StyledTr>

                    {pagedMerchants.map((m, idx) => {
                      const isLast = idx === pagedMerchants.length - 1
                      const pct = grandTotal > 0 ? ((m.total / grandTotal) * 100).toFixed(1) : '0.0'
                      const barPct = grandTotal > 0 ? Math.round((m.total / (merchants[0]?.total || 1)) * 100) : 0
                      return (
                        <StyledTr key={m.merchantName} isLast={isLast}>
                          <StyledTd>
                            <Typography sx={{fontSize: '0.82rem', fontWeight: 700, color: '#424242'}}>
                              {m.merchantName}
                            </Typography>
                          </StyledTd>
                          <StyledTd align='right'>
                            <Box sx={{display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5}}>
                              <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
                                <Typography sx={{fontSize: '0.82rem', fontWeight: 700}}>
                                  {m.total?.toLocaleString()}
                                </Typography>
                                <PctChip chipBg='#e3f2fd' chipColor='#1565c0'>{pct}%</PctChip>
                              </Box>
                              <CellBar pct={barPct} barColor='#1565c0' />
                            </Box>
                          </StyledTd>
                          {dates.map(d => {
                            const count = m.byDate?.[d] || 0
                            return (
                              <StyledTd key={d} align='right'>
                                {count > 0
                                  ? count.toLocaleString()
                                  : <Typography sx={{fontSize: '0.78rem', color: '#bdbdbd'}}>—</Typography>
                                }
                              </StyledTd>
                            )
                          })}
                        </StyledTr>
                      )
                    })}
                  </tbody>
                </StyledTable>
              </Box>
            )}

            {!isLoadingMerchants && filteredMerchants.length > 0 && (
              <PaginationRow>
                <PaginationInfo>
                  Showing {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, filteredMerchants.length)} of {filteredMerchants.length} results
                </PaginationInfo>
                <PaginationBtns>
                  <IconButton size='small' disabled={page === 0} onClick={() => setPage(p => p - 1)}>
                    <ChevronLeftIcon fontSize='small' />
                  </IconButton>
                  <IconButton size='small' disabled={page >= pageCount - 1} onClick={() => setPage(p => p + 1)}>
                    <ChevronRightIcon fontSize='small' />
                  </IconButton>
                </PaginationBtns>
              </PaginationRow>
            )}
          </SectionCard>
        </>
      )}
    </PageWrapper>
  )
}
